/**
 * Pending session return requests (Feature 106).
 *
 * Advisors asking for a sponsored session back. Approving cancels the engagement, and that
 * cancellation is what returns the session — so the button says what it does rather than
 * promising a refund that has no ledger entry behind it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { useToast } from '@/shared/components/Toast';
import {
  decideSessionReturnRequest,
  fetchSessionReturnRequests,
} from '../../services/careConciergeCasesApi';

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
    ?.message ??
  (error as { message?: string })?.message ??
  fallback;

export const SessionReturnRequests = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data: requests } = useQuery({
    queryKey: ['admin', 'care-concierge', 'return-requests', 'PENDING'],
    queryFn: () => fetchSessionReturnRequests('PENDING'),
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      decideSessionReturnRequest(id, approve),
    onSuccess: (_result, variables) => {
      // Both queries move: approving cancels an engagement, which changes the consultation list
      // as well as clearing the request.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'care-concierge'] });
      showToast(
        variables.approve ? 'Session returned to the pool.' : 'Request declined.',
        'success',
      );
    },
    onError: (error) => showToast(errorMessage(error, 'Could not save that decision.'), 'error'),
  });

  // Nothing waiting: no empty state, because this block only exists to be acted on.
  if (!requests || requests.length === 0) return null;

  return (
    <div data-testid="session-return-requests">
      <h3 className="mb-2 text-sm font-semibold text-gray-900">
        Session return requests ({requests.length})
      </h3>

      <Card className="divide-y divide-border">
        {requests.map((request) => (
          <div key={request.id} className="flex flex-wrap items-start justify-between gap-3 p-4">
            <div className="min-w-0">
              <p className="text-sm font-medium">{request.requestedByName}</p>
              <p className="text-xs text-muted-foreground">
                Requested {formatDate(request.requestedAt)}
              </p>
              {request.reason && <p className="mt-1 text-sm text-gray-600">{request.reason}</p>}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: request.id, approve: true })}
              >
                Return the session
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: request.id, approve: false })}
              >
                Decline
              </Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};
